// Picks the mentor's conversational move. Pure signal rules first (no cost);
// only calls the LLM classifier when the signals are ambiguous.
import { segments, stripRole as strip, isUserSeg, isMentorSeg } from "./chatHistory";
import { renderStr } from "./prompts";
import { perLang } from "./langRegex";

// "The user is asking for an explanation" keywords, from
// vocab(lang).explain_request (a regex body; JS-only — the backend has no
// move router). Keeps this rule's own letter-only boundaries.
const explainRe = (lang) => perLang("explain_request", lang,
  (v) => new RegExp(`(^|[^\\p{L}])(${v.explain_request})([^\\p{L}]|$)`, "iu"));

export const MOVE_TYPES = ["question", "deepen", "explain_steps", "lateral", "organic"];
// scenari_futuri deliberately has NO entry here (unlike mappatura's
// map_connection): finding future scenarios is an implicit background goal
// for that mode, not a chat move to announce — actual forking is proposed
// separately (scenarioActionPrompt.js) and shown as an accept/dismiss chip
// on the timeline, never as a repeated "quali scenari vedi?" question. An
// earlier future_projection move that DID ask that literal question caused
// exactly that repetition; removed rather than reworded, since the
// scenario-forking decision never depended on which move was picked here.
export const MODE_MOVES = { mappatura: "map_connection" };

function words(s) {
  return new Set((s || "").toLowerCase().match(/\p{L}+/gu) || []);
}
function jaccard(a, b) {
  const A = words(a), B = words(b);
  if (!A.size || !B.size) return 0;
  let inter = 0;
  for (const w of A) if (B.has(w)) inter++;
  return inter / (A.size + B.size - inter);
}

// Pure: derive the move from the role-labelled history alone.
export function pickMove({ chat, lang = "it" }) {
  const segs = segments(chat);
  const userTurns = segs.filter(isUserSeg).map(strip);
  const mentorTurns = segs.filter(isMentorSeg).map(strip);
  const lastUser = userTurns[userTurns.length - 1] || "";

  // 1. user explicitly asks -> explain
  if (/\?/.test(lastUser) || explainRe(lang).test(lastUser)) {
    return { move_type: "explain_steps", needsLLM: false };
  }
  // 2. mentor repeating itself -> break the loop laterally
  if (mentorTurns.length >= 2 &&
      jaccard(mentorTurns[mentorTurns.length - 1], mentorTurns[mentorTurns.length - 2]) > 0.6) {
    return { move_type: "lateral", needsLLM: false };
  }
  // 3. substantive new input -> dig in
  if (lastUser.length > 120) {
    return { move_type: "deepen", needsLLM: false };
  }
  // 4. very start -> a strategic question
  if (mentorTurns.length === 0) {
    return { move_type: "question", needsLLM: false };
  }
  // 5. ambiguous -> let the model decide
  return { move_type: "question", needsLLM: true };
}

// Finds which candidate appears earliest in `raw`, rather than which one is
// declared first in `candidates`. A garbled model response can mention more
// than one valid word; the one the model actually meant is the one it wrote
// first, not whichever happens to be first in our list.
function firstMatchByPosition(raw, candidates) {
  let best = null;
  let bestIndex = Infinity;
  for (const c of candidates) {
    const m = new RegExp(`\\b${c}\\b`, "i").exec(raw || "");
    if (m && m.index < bestIndex) {
      best = c;
      bestIndex = m.index;
    }
  }
  return best;
}

// Ambiguous case: one short LLM call returning a single word. `generate` is a
// transport-agnostic (prompt -> Promise<string>) function injected by the
// caller. `mode` (optional) adds that mode's own move to the candidate list
// on top of the 5 baseline moves — a Mappatura/Scenari Futuri session still
// sometimes needs a plain question/deepen turn, so the baseline never drops.
export async function classifyMove({ chat, generate, mode, lang = "it" }) {
  const candidates = MODE_MOVES[mode] ? [...MOVE_TYPES, MODE_MOVES[mode]] : MOVE_TYPES;
  try {
    const raw = await generate(renderStr("move_classifier", lang, { chat, moves: candidates.join("|") }));
    const found = firstMatchByPosition(raw, candidates);
    return found || "question";
  } catch (e) {
    return "question";
  }
}
